import { useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { useGetContactInfo } from "api/common/useGetContactInfo";
import { PORTFOLIO_ANALYTICS } from "api/overview/useGetPortfolioAnalytics";
import {
  getPositionsAndFiguresCacheKey,
  markFetchPolicyKeyFresh,
} from "api/utils";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { logger } from "utils/logger";

/**
 * Warms the positions-and-figures analytics cache for every one of the
 * contact's portfolios as soon as the app loads, before the user navigates to
 * the slow "Avkastning"/holdings pages.
 *
 * The analytics calc is computed server-side per portfolio and is slow, so a
 * single prefetch on login (as previously attempted) only covered the one
 * portfolio that happened to render its overview. Multi-portfolio clients were
 * left waiting on the calc when switching portfolios. This loops every
 * top-level portfolio and primes each one.
 *
 * On success we stamp the per-portfolio fetch-policy key as fresh so that when
 * the page is actually opened, getFetchPolicyOptions resolves to "cache-only"
 * and reads instantly from the warmed cache instead of re-issuing the same slow
 * query. The query document, variables and dataKey mirror useGetPortfolioAnalytics
 * exactly so the cache entries line up.
 */
export const usePrefetchPortfolioAnalytics = () => {
  const client = useApolloClient();
  const { linkedContact } = useKeycloak();
  const { selectedContactId } = useGetContractIdData();
  const currentContactId = selectedContactId || linkedContact;

  const { data } = useGetContactInfo(false, selectedContactId);
  const portfolios = data?.portfolios;

  // Track which portfolios we've already kicked off a prefetch for, so re-renders
  // (e.g. navigation) don't re-fire the slow query.
  const prefetchedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!currentContactId || !portfolios?.length) return;

    portfolios.forEach((portfolio) => {
      const portfolioId = portfolio.id;
      if (prefetchedRef.current.has(portfolioId)) return;
      prefetchedRef.current.add(portfolioId);

      const dataKey = getPositionsAndFiguresCacheKey(
        currentContactId,
        portfolioId
      );

      client
        .query({
          query: PORTFOLIO_ANALYTICS,
          variables: {
            ids: portfolioId,
            startDate: undefined,
            endDate: undefined,
            dataKey,
          },
          fetchPolicy: "cache-first",
        })
        .then(() => markFetchPolicyKeyFresh(dataKey))
        .catch((error) => {
          // Let the page-level query retry; just un-mark so a real visit can refetch.
          prefetchedRef.current.delete(portfolioId);
          logger.error("Prefetch of portfolio analytics failed", error);
        });
    });
  }, [client, currentContactId, portfolios]);
};
